import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";

/**
 * BlogsContent — professional blog hub
 * - Hero header with subtitle
 * - Search + tag filters
 * - Modern responsive card grid (Bootstrap 5)
 * - Optional featured section
 */

// Optional AdSense slot (replace ids or remove if not using AdSense yet)
function AdSlot({ adSlot = "5201219465" }) {
  React.useEffect(() => {
    try {
      if (window.adsbygoogle && Array.isArray(window.adsbygoogle)) {
        window.adsbygoogle.push({});
      }
    } catch {}
  }, []);

  return (
    <div className="my-3">
      <ins
        className="adsbygoogle"
        style={{ display: "block" }}
        data-ad-client="ca-pub-9033879555371063"
        data-ad-slot={adSlot}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
}

const BLOGS = [
  {
    to: "/git",
    title: "Git, but Practical: Branching, Reviews & Safe Releases",
    excerpt:
      "A pragmatic guide to Git you’ll actually use day‑to‑day: branching models, rebasing vs. merging, and release checklists.",
    tags: ["Git", "Version Control"],
    updated: "3 days ago",
    read: "6 min",
    featured: true,
  },
  {
    to: "/consul",
    title: "What is Consul? Service Discovery, KV & Mesh in the Real World",
    excerpt:
      "Understand Consul’s building blocks—catalog, KV, health checks, Connect—and where it fits vs. service mesh gateways.",
    tags: ["Consul", "Service Mesh", "HashiCorp"],
    updated: "1 week ago",
    read: "7 min",
    featured: true,
  },
  {
    to: "/cdn",
    title: "Why CDNs Matter: Latency, Caching, and Edge Security",
    excerpt:
      "How a CDN accelerates your app: caching strategies, cache keys, invalidation, TLS, and common gotchas.",
    tags: ["CDN", "Performance", "Caching"],
    updated: "2 weeks ago",
    read: "5 min",
    featured: true,
  },
  {
    to: "/blind75",
    title: "Blind 75: How to Practice (Without Burning Out)",
    excerpt:
      "A structured path through the 75 core coding problems with timeboxing, patterns, and checkpoints.",
    tags: ["Interview", "DSA", "Practice"],
    updated: "4 days ago",
    read: "8 min",
  },
  {
    to: "/prepareForInterview",
    title: "Programming Interview Playbook: From Resume to Offer",
    excerpt:
      "Checklist for pre‑screen, coding, system design, and behavioral rounds—plus templates and follow‑up notes.",
    tags: ["Interview", "Career"],
    updated: "6 days ago",
    read: "9 min",
  },
  {
    to: "/interviewProcess",
    title: "Common Interview Process: What to Expect & How to Prepare",
    excerpt:
      "A clear walkthrough of typical stages across companies, with tips to stand out at each step.",
    tags: ["Interview", "Career"],
    updated: "1 week ago",
    read: "4 min",
  },
];

const allTags = Array.from(new Set(BLOGS.flatMap((b) => b.tags))).sort();

function useFilteredPosts(query, activeTags) {
  const q = query.trim().toLowerCase();
  return useMemo(() => {
    return BLOGS.filter((b) => {
      const matchesQ = !q || `${b.title} ${b.excerpt} ${b.tags.join(" ")}`.toLowerCase().includes(q);
      const matchesTags = activeTags.size === 0 || b.tags.some((t) => activeTags.has(t));
      return matchesQ && matchesTags;
    });
  }, [q, activeTags]);
}

function TagChips({ activeTags, setActiveTags }) {
  const toggle = (t) => {
    const next = new Set(activeTags);
    next.has(t) ? next.delete(t) : next.add(t);
    setActiveTags(next);
  };

  return (
    <div className="d-flex flex-wrap gap-2">
      {allTags.map((t) => (
        <button
          key={t}
          type="button"
          onClick={() => toggle(t)}
          className={`btn btn-sm ${activeTags.has(t) ? "btn-primary" : "btn-outline-secondary"}`}
          aria-pressed={activeTags.has(t) ? "true" : "false"}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

function BlogCard({ post }) {
  return (
    <div className="col">
      <div className="card h-100 shadow-sm border-0">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between mb-1">
            <div className="d-flex align-items-center gap-2 small text-muted">
              <span>{post.read} read</span>
              <span>•</span>
              <span>Updated {post.updated}</span>
            </div>
            {post.featured && <span className="badge bg-warning text-dark">Featured</span>}
          </div>
          <h3 className="h5 mb-2">
            <Link to={post.to} className="stretched-link text-decoration-none">{post.title}</Link>
          </h3>
          <p className="text-body-secondary mb-3">{post.excerpt}</p>
          <div className="d-flex flex-wrap gap-1">
            {post.tags.map((t) => (
              <span key={t} className="badge bg-light text-muted border">{t}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BlogsContent() {
  const [query, setQuery] = useState("");
  const [activeTags, setActiveTags] = useState(new Set());

  const posts = useFilteredPosts(query, activeTags);
  const featured = posts.filter((p) => p.featured);
  const nonFeatured = posts.filter((p) => !p.featured);

  return (
    <div className="container py-3 py-md-4">
      {/* Hero */}
      <header className="text-center mb-4">
        <h1 className="display-6 fw-semibold mb-2">Guides & Playbooks for Modern Developers</h1>
        <p className="text-muted mx-auto" style={{ maxWidth: 820 }}>
          Curated, practical reads on core topics—version control, networking, interview prep, and more. No fluff; just the signal.
        </p>
      </header>

      {/* Search + Tags */}
      <section className="mb-4">
        <div className="card shadow-sm border-0">
          <div className="card-body">
            <form className="d-flex flex-wrap align-items-center gap-2" role="search" aria-label="Blog search">
              <input
                type="search"
                className="form-control flex-grow-1"
                placeholder="Search articles (e.g., Git branching, CDN caching, interview)"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search articles"
              />
              <button type="button" className="btn btn-outline-secondary" onClick={() => setQuery("")}>Clear</button>
            </form>
            <div className="mt-3">
              <TagChips activeTags={activeTags} setActiveTags={setActiveTags} />
            </div>
            <div className="small text-muted mt-2">Showing {posts.length} of {BLOGS.length}</div>
          </div>
        </div>
      </section>

      {/* Featured */}
      {featured.length > 0 && (
        <section className="mb-4" aria-labelledby="featured-heading">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h2 id="featured-heading" className="h5 text-uppercase text-muted m-0">Featured</h2>
            <Link to="/blogs" className="small">View all</Link>
          </div>
          <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3 g-md-4">
            {featured.map((p) => <BlogCard key={p.to} post={p} />)}
          </div>
        </section>
      )}

      {/* Ad (optional) */}
      <AdSlot adSlot="9876543210" />

      {/* All posts */}
      <section className="mb-5" aria-labelledby="all-heading">
        <h2 id="all-heading" className="h5 text-uppercase text-muted mb-2">All Articles</h2>
        <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3 g-md-4">
          {nonFeatured.map((p) => <BlogCard key={p.to} post={p} />)}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="mb-4" aria-labelledby="newsletter-heading">
        <div className="card shadow-sm border-0">
          <div className="card-body p-4 p-md-5">
            <div className="row g-3 align-items-center">
              <div className="col-12 col-md">
                <h2 id="newsletter-heading" className="h5 mb-1">Get new posts in your inbox</h2>
                <p className="text-muted mb-0">One email a month. No spam. Unsubscribe anytime.</p>
              </div>
              <div className="col-12 col-md-auto">
                <form className="d-flex gap-2" onSubmit={(e) => e.preventDefault()}>
                  <input type="email" required className="form-control" placeholder="you@example.com" aria-label="Email address" />
                  <button className="btn btn-primary" type="submit">Subscribe</button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer note */}
      <footer className="text-center text-muted small mt-4">
        © {new Date().getFullYear()} Tech Venture LLC — Built for learners and builders
      </footer>
    </div>
  );
}
